import { TimelineMode } from '@src/editor/commands/TimelineMode';
import { useShortcutScope } from '@src/hooks/shortcuts/useShortcutScope';
import { createTimelineStageShortcutScope } from '@src/services/shortcuts/config/timelineStageShortcuts';
import { CopyElementService } from '@src/services/timeline/editing/CopyElementService';
import { DeleteActiveElementService } from '@src/services/timeline/editing/DeleteActiveElementService';
import { PasteElementService } from '@src/services/timeline/editing/PasteElementService';
import { SplitElementService } from '@src/services/timeline/editing/SplitElementService';
import { SplitSceneService } from '@src/services/timeline/editing/SplitSceneService';
import { TimelineZoomService } from '@src/services/timeline/TimelineZoomService';
import { useEditor, useUserIdentity, useVideoSource } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractTimingsFromEditorElements } from '@src/utility/sceneAnchors';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { runInAction } from 'mobx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTimelineNavigation } from './useTimelineNavigation';
import { useTimelineTracks } from './useTimelineTracks';

import type { SecondaryActionKey } from '@src/components/timeline/TimelineContextMenu';
import type { ElementState } from '@src/renderer/ElementState';
import type React from 'react';

const MIN_TIMELINE_SCALE = 0.1;
const PINCH_MULTIPLIER = 1;

interface UseTimelineProps {
  duration: number;
  frameRate: number;
  enableKeyboardShortcuts?: boolean;
  autoScroll?: boolean;
}

interface PinchState {
  offset: [number, number];
  scale: number;
  velocity: number;
  direction: [number, number];
  maxWidth: number;
  origin: [number, number];
  last: boolean;
}

export interface TimelineClipboard {
  pos: { left: number; top: number } | null;
  action: 'copy' | 'copied' | 'paste';
  tmp: { element: ElementState };
  copied: {
    element: ElementState;
    data: Record<'time' | 'track', number> | null;
  } | null;
  secondaryActions: SecondaryActionKey[];
}

const FALLBACK_MAX_WIDTH = 700;

export const useTimeline = ({
  duration,
  frameRate,
  enableKeyboardShortcuts = true,
  autoScroll = true,
}: UseTimelineProps) => {
  const videoCreator = useVideoCreatorStore();
  const editorStore = useEditor();
  const videoSource = useVideoSource();
  const { tracks, activeTracks } = useTimelineTracks();
  const { enableTimelineCopyPaste } = useFlagsCombination(
    useUserIdentity().currentRole,
  );
  const [maxWidth, setMaxWidth] = useState(FALLBACK_MAX_WIDTH);

  const [isScaling, setIsScaling] = useState(false);
  const [contextMenuState, setContextMenuState] =
    useState<TimelineClipboard | null>(null);
  const [rippleMode, setRippleModeState] = useState<
    'ripple-track' | 'keep-gap'
  >('ripple-track');
  const timelineStageFocusRef = useRef(false);
  const isElementWithinTimelineStage = useCallback(
    (element?: Element | null) => {
      if (!(element instanceof HTMLElement)) {
        return false;
      }
      return Boolean(element.closest('[data-shortcut-scope="timeline-stage"]'));
    },
    [],
  );

  // Refs for scroll and animation
  const animatedRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const prevTimelineScaleDuration = useRef<number | null>(null);

  // Current values
  const scale = videoCreator.timelineScale;
  const currentTime = videoCreator.time;

  const {
    isNavigating,
    navigateToTime,
    navigateToNextFrame,
    navigateToPreviousFrame,
    navigateToNextEditPoint,
    navigateToPreviousEditPoint,
    scrubToTime,
    handleRepositionPlayheadClick,
  } = useTimelineNavigation({
    videoCreator,
    scale,
    frameRate,
    duration,
    tracks,
    activeTracks,
    timelineRef,
  });

  // Auto-scale calculation effect
  const shouldCalcTimelineScale =
    videoCreator.timelineStore.shouldCalcTimelineScale;
  useEffect(() => {
    if (
      duration &&
      duration !== prevTimelineScaleDuration.current &&
      shouldCalcTimelineScale &&
      maxWidth &&
      videoCreator.stateReady
    ) {
      const newScale = (maxWidth - 30) / duration;
      runInAction(() => {
        videoCreator.defaultTimelineScale = newScale;
        videoCreator.timelineScale = newScale * 0.95;
        if (maxWidth !== 700) {
          // FALLBACK_MAX_WIDTH
          videoCreator.timelineStore.setShouldCalcTimelineScale(false);
          prevTimelineScaleDuration.current = duration;
        }
      });
    }
  }, [duration, shouldCalcTimelineScale, maxWidth, videoCreator.stateReady]);

  // Effects for width calculation and scroll tracking
  useEffect(() => {
    const element = document.getElementById('timeline-right');
    if (!element) return;
    const resizeObserver = new ResizeObserver(() => {
      const width = element.clientWidth ?? FALLBACK_MAX_WIDTH;
      setMaxWidth(width);
    });
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  // Auto-scroll functionality
  const setAnimatedScrollPos = useCallback(
    (scrollFactor = 0.1) => {
      if (animatedRef.current && timelineRef.current && autoScroll) {
        const time = videoCreator.time;
        const offsetX = timelineRef.current.scrollLeft;
        const visibleTimeRange = [
          offsetX / scale,
          (offsetX + maxWidth) / scale,
        ];
        const scrollThreshold = videoCreator.isPlaying
          ? visibleTimeRange[1] -
            0.1 * (visibleTimeRange[1] - visibleTimeRange[0])
          : visibleTimeRange[1];

        if (
          time < visibleTimeRange[0] ||
          (time > scrollThreshold && visibleTimeRange[1] < duration) ||
          scrollFactor === 0.5
        ) {
          const scrollTo =
            Math.max(
              0,
              time - scrollFactor * (visibleTimeRange[1] - visibleTimeRange[0]),
            ) * scale;
          timelineRef.current.scrollLeft = scrollTo;
          animatedRef.current.style.left = `-${timelineRef.current.scrollLeft}px`;
        }
      }
    },
    [videoCreator.isPlaying, maxWidth, duration, scale, autoScroll],
  );

  useEffect(() => {
    if (autoScroll) {
      setAnimatedScrollPos();
    }
  }, [currentTime, setAnimatedScrollPos]);

  const prevScale = useRef<number>(scale);
  useEffect(() => {
    // We want setAnimatedScrollPos to be fresh,
    // but don't want it to trigger auto-scroll
    if (prevScale.current === scale) return;
    if (autoScroll) {
      setAnimatedScrollPos(0.5);
    }
    prevScale.current = scale;
  }, [scale, setAnimatedScrollPos]);

  // Pinch/zoom handling
  const handlePinch = useCallback(
    (state: PinchState) => {
      setIsScaling(true);
      const { offset } = state;

      const newTimelineScale = Math.max(
        Math.min(
          videoCreator.maxTimelineScale,
          offset[0] * PINCH_MULTIPLIER * videoCreator.defaultTimelineScale,
        ),
        MIN_TIMELINE_SCALE,
      );
      runInAction(() => (videoCreator.timelineScale = newTimelineScale));

      if (timelineRef.current) {
        if (maxWidth - duration * newTimelineScale > 0) {
          if (animatedRef.current) {
            animatedRef.current.style.left = '0px';
          }
          timelineRef.current.scrollLeft = 0;
          return;
        }

        const positionRect = timelineRef.current.getBoundingClientRect();
        const newOffset =
          videoCreator.time * newTimelineScale -
          (positionRect.right - positionRect.left) / 2;
        timelineRef.current.scrollLeft = Math.max(0, newOffset);
      }

      if (state.last) {
        setIsScaling(false);
      }
    },
    [videoCreator, maxWidth, duration],
  );

  // Zoom service (zoom + fit encapsulation)
  // Note: getDuration reads directly from videoCreator.duration (MobX observable)
  // so it always uses the current value, not a stale closure capture
  const zoomService = useMemo(
    () =>
      new TimelineZoomService({
        videoCreator,
        getDuration: () => videoCreator.duration,
        getMaxWidth: () => maxWidth,
        minTimelineScale: MIN_TIMELINE_SCALE,
      }),
    [videoCreator, maxWidth],
  );

  // Zoom helpers delegate to service (stable refs for components)
  const zoomIn = useCallback(() => zoomService.zoomIn(), [zoomService]);
  const zoomOut = useCallback(() => zoomService.zoomOut(), [zoomService]);
  const zoomToFit = useCallback(() => zoomService.zoomToFit(), [zoomService]);

  /**
   * Expands the timeline scale range to accommodate a longer duration.
   * Called by EndMarker when dragging near the edge and at minimum zoom.
   */
  const expandScaleForDuration = useCallback(
    (newDuration: number) => {
      if (!maxWidth || maxWidth === FALLBACK_MAX_WIDTH) return;
      const newScale = (maxWidth - 30) / newDuration;
      runInAction(() => {
        videoCreator.defaultTimelineScale = newScale;
        videoCreator.timelineScale = newScale * 0.95;
      });
    },
    [maxWidth, videoCreator],
  );

  // Edit service (split + future edit operations)
  const splitService = useMemo(
    () =>
      new SplitElementService({
        editorStore,
        videoCreator,
        videoSource,
      }),
    [editorStore, videoCreator, videoSource],
  );

  const deleteService = useMemo(
    () =>
      new DeleteActiveElementService({
        editorStore,
        videoCreator,
        timelineStore: videoCreator.timelineStore,
        videoSource,
      }),
    [editorStore, videoCreator, videoSource],
  );

  const splitSceneService = useMemo(
    () =>
      new SplitSceneService({
        editorStore,
        videoCreator,
      }),
    [editorStore, videoCreator],
  );

  const copyService = useMemo(
    () =>
      new CopyElementService({
        videoCreator,
        editorStore,
        clipboardHolder: editorStore,
      }),
    [videoCreator, editorStore],
  );

  const pasteService = useMemo(
    () =>
      new PasteElementService({
        videoCreator,
        editorStore,
        videoSource,
        clipboardHolder: editorStore,
      }),
    [videoCreator, editorStore, videoSource],
  );

  const splitActiveTrackElementAtCurrentTime = useCallback(async () => {
    const result =
      await splitService.splitActiveTrackElementAtCurrentTime(activeTracks);
    return result;
  }, [splitService, videoCreator, activeTracks]);

  /**
   * Splits the scene at the current playhead position.
   * @returns true if a scene was split, false if playhead is not over any scene.
   */
  const splitSceneAtPlayhead = useCallback(() => {
    const elements = Array.from(editorStore.tracksMap.values()).flat();
    const elementTimings = extractTimingsFromEditorElements(
      elements,
      el =>
        (el.type === 'video' || el.type === 'composition') &&
        videoCreator.isOriginalVideoElement(el.toJSON()),
    );
    return splitSceneService.splitSceneAt(videoCreator.time, elementTimings);
  }, [splitSceneService, videoCreator, editorStore]);

  const deleteActiveElement = useCallback(async () => {
    return await deleteService.removeActiveOrSelection();
  }, [deleteService]);

  const copyActiveElement = useCallback(() => {
    return copyService.copy();
  }, [copyService]);

  const pasteElement = useCallback(() => {
    return pasteService.paste();
  }, [pasteService]);

  // Navigation (delegated to useTimelineNavigation)

  // Shortcut service instance (recreated when dependencies affecting shortcut behavior change)
  const timelineStageScope = useMemo(() => {
    const isScopeActive = () => {
      if (typeof document === 'undefined') {
        return false;
      }

      const withinArea = isElementWithinTimelineStage(document.activeElement);

      timelineStageFocusRef.current = withinArea;
      return withinArea;
    };

    return createTimelineStageShortcutScope({
      frameRate,
      getTime: () => videoCreator.time,
      getDuration: () => duration,
      navigateToTime: t => navigateToTime(t),
      navigateToNextFrame: () => navigateToNextFrame(),
      navigateToPreviousFrame: () => navigateToPreviousFrame(),
      navigateToNextEditPoint: () => navigateToNextEditPoint(),
      navigateToPreviousEditPoint: () => navigateToPreviousEditPoint(),
      zoomIn: () => zoomIn(),
      zoomOut: () => zoomOut(),
      zoomToFit: () => zoomToFit(),
      splitActiveTrackElementAtCurrentTime: () =>
        splitActiveTrackElementAtCurrentTime(),
      deleteActiveElement: () => deleteActiveElement(),
      copyActiveElement: () => copyActiveElement(),
      pasteElement: () => pasteElement(),
      enableCopyPaste: enableTimelineCopyPaste,
      isActive: isScopeActive,
    });
  }, [
    frameRate,
    videoCreator,
    duration,
    navigateToTime,
    navigateToNextFrame,
    navigateToPreviousFrame,
    navigateToNextEditPoint,
    navigateToPreviousEditPoint,
    zoomIn,
    zoomOut,
    zoomToFit,
    splitActiveTrackElementAtCurrentTime,
    deleteActiveElement,
    copyActiveElement,
    pasteElement,
    enableTimelineCopyPaste,
  ]);

  useShortcutScope(timelineStageScope, { enabled: enableKeyboardShortcuts });

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const updateFocusState = (event: Event) => {
      timelineStageFocusRef.current = isElementWithinTimelineStage(
        event.target instanceof Element ? event.target : null,
      );
    };

    document.addEventListener('pointerdown', updateFocusState);
    document.addEventListener('focusin', updateFocusState);
    return () => {
      document.removeEventListener('pointerdown', updateFocusState);
      document.removeEventListener('focusin', updateFocusState);
    };
  }, [isElementWithinTimelineStage]);

  // Context menu handling
  const showContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, element: ElementState) => {
      e.preventDefault();

      const getContextSecondaryActions = (element: ElementState) => {
        const result: TimelineClipboard['secondaryActions'] = [];
        const maybeExtraElementData =
          videoCreator.currentVideo?.extraElementData[element.source.id];
        const hasVolumeKeyPoints =
          maybeExtraElementData !== undefined &&
          maybeExtraElementData !== null &&
          'volumeKeyPoints' in maybeExtraElementData;
        if (
          element?.source?.id &&
          hasVolumeKeyPoints &&
          maybeExtraElementData['volumeKeyPoints']?.length
        ) {
          // Per-keypoint delete only when a specific keypoint is selected.
          if (videoCreator.selectedVolumeKeyPoint) {
            result.push('deleteSelectedKeypoint');
          }
          result.push('clearVolumeKeyPoints');
        }
        return result;
      };

      const contextState: TimelineClipboard = {
        pos: { left: e.clientX, top: e.clientY },
        action: 'copy',
        tmp: { element },
        copied: videoCreator.timelineClipboard?.copied || null,
        secondaryActions: getContextSecondaryActions(element),
      };

      setContextMenuState(contextState);
      videoCreator.timelineClipboard = contextState;
    },
    [videoCreator],
  );

  const hideContextMenu = useCallback(() => {
    setContextMenuState(null);
    if (videoCreator.timelineClipboard) {
      videoCreator.timelineClipboard.pos = null;
    }
  }, [videoCreator]);

  const handleContextAction = useCallback(() => {
    // Handle context menu actions
    hideContextMenu();
  }, [hideContextMenu]);

  const setRippleMode = useCallback(
    (mode: 'ripple-track' | 'keep-gap') => {
      setRippleModeState(mode);
      const timelineMode =
        mode === 'ripple-track' ? TimelineMode.RIPPLE : TimelineMode.DEFAULT;
      editorStore.editor.setTimelineMode(timelineMode);
    },
    [editorStore],
  );

  return {
    // Refs
    animatedRef,
    timelineRef,

    // State
    scale,
    isScaling,
    currentTime,
    isNavigating,
    contextMenuState,
    maxWidth,

    // Scaling
    handlePinch,
    zoomIn,
    zoomOut,
    zoomToFit,
    expandScaleForDuration,

    // Editing
    splitActiveTrackElementAtCurrentTime,
    splitSceneAtPlayhead,
    deleteActiveElement,

    // Navigation
    handleRepositionPlayheadClick,
    navigateToTime,
    navigateToNextFrame,
    navigateToPreviousFrame,
    navigateToNextEditPoint,
    navigateToPreviousEditPoint,
    scrubToTime,

    // Context Menu
    showContextMenu,
    hideContextMenu,
    handleContextAction,

    // Ripple Mode
    rippleMode,
    setRippleMode,
  };
};
